import { useState, useEffect } from "react";
import {
  THRESHOLD_DEFAULTS,
  ALLOWED_THRESHOLD_KEYS,
  type ThresholdOverrides,
} from "../../../constants/thresholdDefaults";

const ENABLED_KEY = "dealos.thresholdOverridesEnabled";
const OVERRIDES_KEY = "dealos.thresholdOverrides";

const DECIMAL = 10;

export interface ThresholdOverridesState {
  enabled: boolean;
  overrides: ThresholdOverrides;
  toggle: (enabled: boolean) => void;
  update: (key: keyof ThresholdOverrides, value: string) => void;
  reset: () => void;
}

/** Keep only the known keys, and only where the stored value is a real number. */
function validateStored(parsed: Record<string, unknown>): ThresholdOverrides {
  const validated: ThresholdOverrides = { ...THRESHOLD_DEFAULTS };

  for (const key of ALLOWED_THRESHOLD_KEYS) {
    const num = key in parsed ? Number(parsed[key]) : Number.NaN;
    if (Number.isFinite(num)) {
      validated[key] = num;
    }
  }

  return validated;
}

/** Read the persisted overrides, ignoring anything unparseable. */
function readStoredOverrides(): ThresholdOverrides | null {
  try {
    const stored = localStorage.getItem(OVERRIDES_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") return null;

    return validateStored(parsed as Record<string, unknown>);
  } catch {
    // Ignore parse errors
    return null;
  }
}

/** Search-threshold overrides, persisted to localStorage only (no API). */
export function useThresholdOverrides(): ThresholdOverridesState {
  const [enabled, setEnabled] = useState(false);
  const [overrides, setOverrides] = useState<ThresholdOverrides>({ ...THRESHOLD_DEFAULTS });

  // Load threshold overrides from localStorage on mount (with validation)
  useEffect(() => {
    setEnabled(localStorage.getItem(ENABLED_KEY) === "true");
    const stored = readStoredOverrides();
    if (stored) {
      setOverrides(stored);
    }
  }, []);

  const persist = (next: ThresholdOverrides): void => {
    setOverrides(next);
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(next));
  };

  const toggle = (next: boolean): void => {
    setEnabled(next);
    localStorage.setItem(ENABLED_KEY, String(next));
  };

  const update = (key: keyof ThresholdOverrides, value: string): void => {
    const numValue = key === "page_size" ? parseInt(value, DECIMAL) : parseFloat(value);
    if (!isNaN(numValue)) {
      persist({ ...overrides, [key]: numValue });
    }
  };

  const reset = (): void => persist({ ...THRESHOLD_DEFAULTS });

  return { enabled, overrides, toggle, update, reset };
}
